import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';
import authService from '../services/authService';

interface OnboardingUrl {
    type: string;
    title: string;
    url: string;
    expirationDate: string;
}

interface KycAlert {
    companyId: string;
    companyName: string;
    pendingDocumentsCount: number;
    onboardingUrls: OnboardingUrl[];
}

interface KycContextData {
    hasAlerts: boolean;
    alertsCount: number;
    alerts: KycAlert[];
    loading: boolean;
    checkKycAlerts: () => Promise<void>;
    resetKyc: () => void;
}

const KycContext = createContext<KycContextData>({} as KycContextData);

export function KycProvider({ children }: { children: ReactNode }) {
    const [hasAlerts, setHasAlerts] = useState(false);
    const [alertsCount, setAlertsCount] = useState(0);
    const [alerts, setAlerts] = useState<KycAlert[]>([]);
    const [loading, setLoading] = useState(false);

    const checkKycAlerts = async () => {
        try {
            setLoading(true);
            const token = authService.getToken();

            if (!token) {
                resetKyc();
                return;
            }

            const response = await api.get('/api/kyc/alerts', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.data && response.data.success) {
                const data = response.data.data;
                setHasAlerts(data.hasAlerts || false);
                setAlertsCount(data.alertsCount || 0);
                setAlerts(data.alerts || []);
            }
        } catch (error) {
            console.error('Erro ao verificar alertas KYC:', error);
            // Não quebra o fluxo se houver erro
            resetKyc();
        } finally {
            setLoading(false);
        }
    };

    const resetKyc = () => {
        setHasAlerts(false);
        setAlertsCount(0);
        setAlerts([]);
    };

    // Verificar alertas ao montar o componente
    useEffect(() => {
        const token = authService.getToken();
        if (token) {
            checkKycAlerts();
        }
    }, []);

    // Listener para eventos de autenticação
    useEffect(() => {
        const handleAuthChange = (event: CustomEvent) => {
            if (event.detail.type === 'logout') {
                resetKyc();
            } else if (event.detail.type === 'login') {
                checkKycAlerts();
            }
        };

        window.addEventListener('authChange' as any, handleAuthChange as any);

        return () => {
            window.removeEventListener('authChange' as any, handleAuthChange as any);
        };
    }, []);

    return (
        <KycContext.Provider value={{
            hasAlerts,
            alertsCount,
            alerts,
            loading,
            checkKycAlerts,
            resetKyc
        }}>
            {children}
        </KycContext.Provider>
    );
}

export function useKyc() {
    const context = useContext(KycContext);
    if (!context) {
        throw new Error('useKyc must be used within a KycProvider');
    }
    return context;
}
